import { useEffect, useState } from 'react';

import * as monthUtils from '@actual-app/core/shared/months';
import type { IntegerAmount } from '@actual-app/core/shared/util';
import type { CategoryEntity } from '@actual-app/core/types/models';

import { useFeatureFlag } from '#hooks/useFeatureFlag';
import { useSheetValue } from '#hooks/useSheetValue';
import { useSpreadsheet } from '#hooks/useSpreadsheet';
import { envelopeBudget } from '#spreadsheet/bindings';

import type { DatosCategoriaMes } from './objetivos';
import { useIgnorarMes } from './useIgnorarMes';

/**
 * Valores vivos de la categoría del teclado de asignar: objetivo, asignado,
 * disponible, si está «ignorada este mes» y lo asignado el mes anterior. Con
 * ellos el teclado elige el botón principal (`botonTeclado.ts`), igual en el
 * Plan y en «Asignar el mes». Sin plantillas de objetivo, `goal` es `null`.
 */
export function useDatosTeclado(
  category: CategoryEntity | null | undefined,
  month: string,
): {
  datos: DatosCategoriaMes;
  asignadoMesPasado: IntegerAmount | null;
  objetivosActivos: boolean;
  setIgnorada: (ignorar: boolean) => Promise<void>;
} {
  const objetivosActivos = useFeatureFlag('goalTemplatesEnabled');
  const bindings = envelopeBudget;
  const id = category?.id ?? '';

  type Hoja = 'envelope-budget';
  const goal = useSheetValue<Hoja, 'goal'>(bindings.catGoal(id));
  const longGoal = useSheetValue<Hoja, 'long-goal'>(bindings.catLongGoal(id));
  const asignado = useSheetValue<Hoja, 'budget'>(bindings.catBudgeted(id)) ?? 0;
  const balance = useSheetValue<Hoja, 'leftover'>(bindings.catBalance(id)) ?? 0;
  const { ignorada, setIgnorada } = useIgnorarMes(id, month);
  const asignadoMesPasado = useAsignadoMesPasado(id, month);

  return {
    datos: {
      goal: category && objetivosActivos ? (goal ?? null) : null,
      longGoal: longGoal === 1,
      budgeted: asignado,
      balance,
      spent: 0,
      ignorada,
    },
    asignadoMesPasado,
    objetivosActivos,
    setIgnorada,
  };
}

/**
 * Lo asignado a la categoría el mes anterior a `month` (`budget-<id>` de la
 * hoja de ese mes); `null` mientras no se sabe o si esa hoja no existe.
 */
function useAsignadoMesPasado(
  categoryId: string,
  month: string,
): IntegerAmount | null {
  const spreadsheet = useSpreadsheet();
  const [valor, setValor] = useState<IntegerAmount | null>(null);

  useEffect(() => {
    setValor(null);
    if (!categoryId || !month) {
      return;
    }
    const hoja = monthUtils.sheetForMonth(monthUtils.prevMonth(month));
    return spreadsheet.bind(hoja, { name: `budget-${categoryId}` }, result => {
      setValor(typeof result.value === 'number' ? result.value : null);
    });
  }, [categoryId, month, spreadsheet]);

  return valor;
}
